import type {
  FormattedPriceSettings,
  TvShowEpisode,
  TvShowSeason,
} from '@setplex/tria-api'
import type {
  EpisodeWithEpisodePriceSettingsParams,
  EpisodeWithSeasonPriceSettingsParams,
  EpisodeWithTvShowPriceSettingsParams,
  IsPayForParams,
  MediaPriceSettings,
  PriceSettingsByPaymentMethodParams,
  SeasonWithSeasonPriceSettingsParams,
  TvShowEpisodePayment,
} from '../interfaces'

import { PriceSettingsPaymentMethod } from '@setplex/tria-api'

export const getPriceSettingsByPaymentMethod = ({
  priceSettings,
  paymentMethod,
}: PriceSettingsByPaymentMethodParams): MediaPriceSettings => {
  if (!Array.isArray(priceSettings)) return null

  return priceSettings?.filter((item) => item.paymentMethod === paymentMethod)
}

export const getPerItemPriceSettingsOnly = (
  priceSettings?: MediaPriceSettings
): MediaPriceSettings => {
  return getPriceSettingsByPaymentMethod({
    priceSettings,
    paymentMethod: PriceSettingsPaymentMethod.PerItem,
  })
}

export const getPerSetPriceSettingsOnly = (
  priceSettings?: MediaPriceSettings
): MediaPriceSettings => {
  return getPriceSettingsByPaymentMethod({
    priceSettings,
    paymentMethod: PriceSettingsPaymentMethod.PerSet,
  })
}

export const hasMediaPerSetPriceSettingsOnly = (
  priceSettings?: MediaPriceSettings
): boolean => {
  const perSetPriceSettings = getPerSetPriceSettingsOnly(priceSettings)
  const perItemPriceSettings = getPerItemPriceSettingsOnly(priceSettings)

  const hasBundleId = Boolean(perSetPriceSettings?.[0]?.bundle?.id)

  return (
    Boolean(perSetPriceSettings?.length) &&
    !perItemPriceSettings?.length &&
    hasBundleId
  )
}

export const isPayForBundle = ({
  priceSettings,
  free,
  purchaseInfo,
}: IsPayForParams): boolean => {
  const isPaidContent = !free && !purchaseInfo
  const hasPerSetPriceSettingOnly =
    hasMediaPerSetPriceSettingsOnly(priceSettings)

  return isPaidContent && hasPerSetPriceSettingOnly
}

export const isPayForItem = ({
  priceSettings,
  free,
  purchaseInfo,
}: IsPayForParams): boolean => {
  const isPaidContent = !free && !purchaseInfo
  const perItemPriceSettings = getPerItemPriceSettingsOnly(priceSettings)

  return isPaidContent && Boolean(perItemPriceSettings?.length)
}

export const hasPayForBundle = ({
  priceSettings,
  free,
  purchaseInfo,
}: IsPayForParams): boolean => {
  const isPaidContent = !free && !purchaseInfo
  const perSetPriceSettings = getPerSetPriceSettingsOnly(priceSettings)

  return isPaidContent && Boolean(perSetPriceSettings?.length)
}

export const isEpisodeWithEpisodePriceSettings = ({
  episodePriceSettings,
  free,
  purchaseInfo,
}: EpisodeWithEpisodePriceSettingsParams): boolean => {
  return !free && !purchaseInfo && Boolean(episodePriceSettings?.length)
}

export const isItemWithSeasonPriceSettings = ({
  seasonPriceSettings,
  free,
  purchaseInfo,
}: EpisodeWithSeasonPriceSettingsParams): boolean => {
  return !free && !purchaseInfo && Boolean(seasonPriceSettings?.length)
}

export const isItemWithTvShowPriceSettings = ({
  tvShowPriceSettings,
  free,
  purchaseInfo,
}: EpisodeWithTvShowPriceSettingsParams): boolean => {
  return !free && !purchaseInfo && Boolean(tvShowPriceSettings?.length)
}

export const isPayForItemEpisode = ({
  episodePriceSettings,
  free,
  purchaseInfo,
}: EpisodeWithEpisodePriceSettingsParams): boolean => {
  const isWithEpisodePriceSettings = isEpisodeWithEpisodePriceSettings({
    episodePriceSettings,
    free,
    purchaseInfo,
  })

  const isItemPayment = isPayForItem({
    priceSettings: episodePriceSettings,
    free,
    purchaseInfo,
  })

  return isWithEpisodePriceSettings && isItemPayment
}

export const isPayForItemSeason = ({
  seasonPriceSettings,
  free,
  purchaseInfo,
}:
  | EpisodeWithSeasonPriceSettingsParams
  | SeasonWithSeasonPriceSettingsParams): boolean => {
  const isWithSeasonPriceSettings = isItemWithSeasonPriceSettings({
    seasonPriceSettings,
    free,
    purchaseInfo,
  })

  const isItemPayment = isPayForItem({
    priceSettings: seasonPriceSettings,
    free,
    purchaseInfo,
  })

  return isWithSeasonPriceSettings && isItemPayment
}

export const isPayForItemTvShow = ({
  tvShowPriceSettings,
  free,
  purchaseInfo,
}: TvShowSeason | TvShowEpisodePayment): boolean => {
  const isWithTvShowPriceSettings = isItemWithTvShowPriceSettings({
    tvShowPriceSettings,
    free: Boolean(free),
    purchaseInfo: purchaseInfo || null,
  })

  const isItemPayment = isPayForItem({
    priceSettings: tvShowPriceSettings,
    free,
    purchaseInfo,
  })

  return isWithTvShowPriceSettings && isItemPayment
}

export const isPayForBundleItemEpisode = ({
  episodePriceSettings,
  free,
  purchaseInfo,
}: EpisodeWithEpisodePriceSettingsParams): boolean => {
  const isWithEpisodePriceSettings = isEpisodeWithEpisodePriceSettings({
    episodePriceSettings,
    free,
    purchaseInfo,
  })

  const isBundleItemPayment = isPayForBundle({
    priceSettings: episodePriceSettings,
    free,
    purchaseInfo,
  })

  return isWithEpisodePriceSettings && isBundleItemPayment
}

export const isPayForBundleItemSeason = ({
  seasonPriceSettings,
  free,
  purchaseInfo,
}: TvShowSeason | TvShowEpisodePayment): boolean => {
  const isWithSeasonPriceSettings = isItemWithSeasonPriceSettings({
    seasonPriceSettings,
    free: Boolean(free),
    purchaseInfo,
  })

  const isBundleItemPayment = isPayForBundle({
    priceSettings: seasonPriceSettings,
    free,
    purchaseInfo,
  })

  return isWithSeasonPriceSettings && isBundleItemPayment
}

export const isPayForBundleItemTvShow = ({
  tvShowPriceSettings,
  free,
  purchaseInfo,
}: TvShowSeason | TvShowEpisodePayment): boolean => {
  const isWithTvShowPriceSettings = isItemWithTvShowPriceSettings({
    tvShowPriceSettings,
    free: Boolean(free),
    purchaseInfo: purchaseInfo || null,
  })

  const isBundleItemPayment = isPayForBundle({
    priceSettings: tvShowPriceSettings,
    free,
    purchaseInfo,
  })

  return isWithTvShowPriceSettings && isBundleItemPayment
}

const isIdInPriceSettings = ({
  priceSettings,
  id,
}: {
  priceSettings: MediaPriceSettings
  id?: number
}): boolean => {
  return Boolean(
    priceSettings?.some(({ priceSettingsId }) => priceSettingsId === id)
  )
}

export const filterEpisodePriceSettingsOnly = (
  episode: TvShowEpisodePayment
): MediaPriceSettings => {
  if (!episode.episodePriceSettings) return null

  return episode.episodePriceSettings.filter(({ priceSettingsId }) => {
    const isSeasonPriceInEpisodePrice = isIdInPriceSettings({
      priceSettings: episode.seasonPriceSettings,
      id: priceSettingsId,
    })
    const isTvShowPriceInEpisodePrice = isIdInPriceSettings({
      priceSettings: episode.tvShowPriceSettings,
      id: priceSettingsId,
    })
    return !isSeasonPriceInEpisodePrice && !isTvShowPriceInEpisodePrice
  })
}

export const filterSeasonPriceSettingsOnly = (
  episode: TvShowEpisodePayment | TvShowSeason
): MediaPriceSettings => {
  const tvShowPriceSettings = episode.tvShowPriceSettings
  const seasonPriceSettings = episode.seasonPriceSettings
  if (!seasonPriceSettings || !tvShowPriceSettings) return null

  return seasonPriceSettings.filter(({ priceSettingsId }) => {
    const isTvShowPriceInEpisodePrice = isIdInPriceSettings({
      priceSettings: tvShowPriceSettings,
      id: priceSettingsId,
    })
    return !isTvShowPriceInEpisodePrice
  })
}

export const getPaymentTvShowEpisodePriceSettings = ({
  episodePriceSettings,
  seasonPriceSettings,
  tvShowPriceSettings,
}: TvShowEpisode | TvShowEpisodePayment):
  | FormattedPriceSettings[]
  | undefined => {
  if (episodePriceSettings && episodePriceSettings.length > 0) {
    return episodePriceSettings
  }

  if (seasonPriceSettings && seasonPriceSettings.length > 0) {
    return seasonPriceSettings
  }

  if (tvShowPriceSettings && tvShowPriceSettings.length > 0) {
    return tvShowPriceSettings
  }
}
